/**
 * NR Swiper Component JavaScript
 * 处理轮播组件的交互逻辑
 */

class NRSwiper {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    // 确保 Swiper 库已加载
    this.waitForSwiper().then(() => {
      this.initSwipers();
    }).catch((error) => {
      console.error('Failed to initialize NRSwiper:', error);
    });
  }

  waitForSwiper() {
    return new Promise((resolve, reject) => {
      if (window.Swiper) {
        resolve(window.Swiper);
        return;
      }

      // 等待 Swiper 加载完成
      let attempts = 0;
      const maxAttempts = 50; // 最多等待 5 秒

      const checkSwiper = () => {
        attempts++;

        if (window.Swiper) {
          resolve(window.Swiper);
        } else if (attempts >= maxAttempts) {
          reject(new Error('Swiper library not loaded within timeout'));
        } else {
          setTimeout(checkSwiper, 100);
        }
      };

      checkSwiper();
    });
  }

  initSwipers() {
    const swiperElements = document.querySelectorAll('[data-swiper-id]');

    swiperElements.forEach((element, index) => {
      try {
        const swiperId = element.dataset.swiperId;
        const swiperType = element.dataset.swiperType;
        const initialSlide = parseInt(element.dataset.initialSlide) || 0;
        const totalItems = parseInt(element.dataset.total) || 0;
        const autoplayDelay = parseInt(element.dataset.autoplayDelay) ?? 3000;
        const spaceBetween = parseInt(element.dataset.spaceBetween);
        // 正确处理 centeredSlides：'true' 字符串转为 true，'false' 字符串转为 false，未定义时使用默认值 true
        const centeredSlidesValue = element.dataset.centeredSlides;
        const centeredSlides = centeredSlidesValue === undefined ? true : centeredSlidesValue === 'true';
        const loop = element.dataset.loop === 'true';
        const slidesPerViewMobile = element.dataset.slidesPerViewMobile;
        const slidesPerViewTablet = element.dataset.slidesPerViewTablet;
        const slidesPerViewDesktop = element.dataset.slidesPerViewDesktop;
        const effect = element.dataset.effect;

        if (totalItems <= 1) {
          return;
        }

        const swiperOptions = this.getSwiperOptions(swiperType, initialSlide, autoplayDelay, swiperId, {
          spaceBetween: spaceBetween,
          centeredSlides: centeredSlides,
          loop: loop,
          slidesPerViewMobile: slidesPerViewMobile,
          slidesPerViewTablet: slidesPerViewTablet,
          slidesPerViewDesktop: slidesPerViewDesktop,
          effect: effect
        });
        const container = element.querySelector('.swiper');

        if (!container) {
          return;
        }

        // 创建 Swiper 实例
        const swiper = new window.Swiper(container, swiperOptions);

        // 存储 loop 参数到 swiper 实例的 customData 中，以便后续使用
        swiper.customData = { loop: loop };
        this.swipers.set(swiperId, swiper);
        this.initPagination(swiperId, swiper, totalItems, loop);

        // 初始化分页器状态
        this.updatePagination(swiperId, initialSlide);
      } catch (error) {
        console.error('Error initializing swiper:', error);
      }
    });
  }

  /**
   * 构建响应式 breakpoints 配置
   * @param {string|number} mobile - 移动端 slidesPerView (<600px)
   * @param {string|number} tablet - 平板端 slidesPerView (600px-1023px)
   * @param {string|number} desktop - PC端 slidesPerView (≥1024px)
   * @returns {Object} 包含 slidesPerView 和 breakpoints 的配置对象
   */
  buildBreakpointsConfig(mobile, tablet, desktop) {
    const config = {
      slidesPerView: 'auto',
      breakpoints: {}
    };

    // 移动端配置（默认，<600px）
    if (mobile) {
      config.slidesPerView = mobile === 'auto' ? 'auto' : parseFloat(mobile);
    } else {
      config.slidesPerView = 1;
    }

    // 平板端配置（600px-1023px）
    if (tablet) {
      const tabletValue = tablet === 'auto' ? 'auto' : parseFloat(tablet);
      config.breakpoints[600] = {
        slidesPerView: tabletValue
      };
    }

    // PC端配置（≥1024px）
    if (desktop) {
      const desktopValue = desktop === 'auto' ? 'auto' : parseFloat(desktop);
      config.breakpoints[1024] = {
        slidesPerView: desktopValue
      };
    } else if (!tablet) {
      // 如果没有设置平板端和PC端，PC端默认使用 auto
      config.breakpoints[1024] = {
        slidesPerView: 'auto'
      };
    }

    return config;
  }

  getSwiperOptions(type, initialSlide, autoplayDelay, swiperId, customOptions = {}) {
    const baseOptions = {
      initialSlide: initialSlide,
      allowTouchMove: true,
      freeMode: false,
      on: {
        slideChange: (swiper) => {
          this.updatePagination(swiperId, swiper.activeIndex);
        }
      }
    };

    if (type === 'product') {
      const productOptions = {
        ...baseOptions,
        slidesPerView: 1,
        spaceBetween: customOptions.spaceBetween !== undefined ? customOptions.spaceBetween : 0
      };

      // 如果传入了 effect，则设置效果
      if (customOptions.effect && customOptions.effect.trim() !== '') {
        productOptions.effect = customOptions.effect;
      }

      return productOptions;
    } else if (type === 'card') {
      const cardOptions = {
        ...baseOptions,
        spaceBetween: customOptions.spaceBetween !== undefined ? customOptions.spaceBetween : 16,
        centeredSlides: customOptions.centeredSlides !== undefined ? customOptions.centeredSlides : true,
        loop: customOptions.loop !== undefined ? customOptions.loop : false
      };

      // 如果传入了 effect，则设置效果
      if (customOptions.effect && customOptions.effect.trim() !== '') {
        cardOptions.effect = customOptions.effect;
      }

      // 使用独立方法构建响应式 breakpoints 配置
      const breakpointsConfig = this.buildBreakpointsConfig(
        customOptions.slidesPerViewMobile,
        customOptions.slidesPerViewTablet,
        customOptions.slidesPerViewDesktop
      );

      cardOptions.slidesPerView = breakpointsConfig.slidesPerView;
      if (Object.keys(breakpointsConfig.breakpoints).length > 0) {
        cardOptions.breakpoints = breakpointsConfig.breakpoints;
      }

      // 只有当 autoplayDelay > 0 时才启用自动播放
      if (autoplayDelay > 0) {
        cardOptions.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        };
      }

      return cardOptions;
    }

    return baseOptions;
  }

  initPagination(swiperId, swiper, totalItems, loop = false) {
    const paginationElement = document.querySelector(`[data-swiper-pagination="${swiperId}"]`);
    if (!paginationElement) return;

    const paginationItems = paginationElement.querySelectorAll('[data-swiper-pagination-item]');

    paginationItems.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();

        const itemType = item.dataset.swiperPaginationItem;

        if (itemType === 'prev') {
          // 上一页 - 根据 loop 参数决定是否支持循环
          const newIndex = swiper.activeIndex - 1;
          if (loop) {
            swiper.slidePrev();
          } else {
            swiper.slideTo(Math.max(0, newIndex));
          }
          if (item.dataset.canClickEvent == 'true') {
            window.dataLayer.push({
              event:"ga4Event",
              event_name: item.dataset.eventName,
              event_parameters:{
                button_name: 'arrow prev',
                module_name: item.dataset.moduleName
              }
            });
          }
        } else if (itemType === 'next') {
          // 下一页 - 根据 loop 参数决定是否支持循环
          const newIndex = swiper.activeIndex + 1;
          if (loop) {
            swiper.slideNext();
          } else {
            swiper.slideTo(Math.min(totalItems - 1, newIndex));
          }

          if (item.dataset.canClickEvent == 'true') {
            window.dataLayer.push({
              event:"ga4Event",
              event_name: item.dataset.eventName,
              event_parameters:{
                button_name: 'arrow next',
                module_name: item.dataset.moduleName
              }
            });
          }
        } else {
          // 数字索引（product 和 card 分页器）
          const index = parseInt(itemType);
          if (!isNaN(index)) {
            swiper.slideTo(index);
          }
        }
      });
    });
  }

  updatePagination(swiperId, activeIndex) {
    const paginationElement = document.querySelector(`[data-swiper-pagination="${swiperId}"]`);
    if (!paginationElement) {
      return;
    }

    // 获取对应的 swiper 实例来计算总项目数
    const swiper = this.swipers.get(swiperId);
    const totalItems = swiper ? swiper.slides.length : 0;

    // 根据分页器类型处理不同的更新逻辑
    const isProductPagination = paginationElement.classList.contains('nr-swiper__pagination--product');
    const isCardPagination = paginationElement.classList.contains('nr-swiper__pagination--card');
    const isFloatingPagination = paginationElement.classList.contains('nr-swiper__pagination--floating');

    // 获取颜色值（从 CSS 变量或默认值）
    const defaultColor = getComputedStyle(paginationElement).getPropertyValue('--nr-pagination-color') || '#DBDBDD';
    const activeColor = getComputedStyle(paginationElement).getPropertyValue('--nr-pagination-color-active') || '#838289';

    if (isProductPagination) {
      // 产品轮播分页器 - 更新指示器位置
      const indicator = paginationElement.querySelector('[data-swiper-pagination-indicator]');
      if (indicator) {
        indicator.style.transform = `translateX(${activeIndex * 100}%)`;
      }

      // 更新分页器项目状态（产品轮播的颜色）
      const paginationItems = paginationElement.querySelectorAll('[data-swiper-pagination-item]');
      paginationItems.forEach((item, index) => {
        if (index === activeIndex) {
          item.style.color = activeColor;
          item.classList.add('nr-is-active');
        } else {
          item.style.color = defaultColor;
          item.classList.remove('nr-is-active');
        }
      });
    } else if (isCardPagination) {
      // 卡片轮播分页器 - 更新圆点宽度和颜色
      const paginationItems = paginationElement.querySelectorAll('[data-swiper-pagination-item]');

      paginationItems.forEach((item, index) => {
        if (index === activeIndex) {
          item.style.width = '30px';
          item.style.backgroundColor = activeColor;
          item.classList.add('nr-is-active');
        } else {
          item.style.width = '10px';
          item.style.backgroundColor = defaultColor;
          item.classList.remove('nr-is-active');
        }
      });
    } else if (isFloatingPagination) {
      // 浮动分页器 - 更新箭头状态
      const prevButton = paginationElement.querySelector('[data-swiper-pagination-item="prev"]');
      const nextButton = paginationElement.querySelector('[data-swiper-pagination-item="next"]');

      // 获取 loop 参数
      const loop = swiper && swiper.customData ? swiper.customData.loop : false;

      if (prevButton) {
        if (loop) {
          // 循环模式下，永远不禁用上一页按钮
          prevButton.disabled = false;
          prevButton.classList.remove('nr-is-disabled');
        } else {
          // 非循环模式下，使用 Swiper 的 isBeginning 属性判断是否在开头
          // 这样可以正确处理 slidesPerView > 1 的情况
          const isBeginning = swiper && swiper.isBeginning !== undefined ? swiper.isBeginning : (activeIndex === 0);
          if (isBeginning) {
            prevButton.disabled = true;
            prevButton.classList.add('nr-is-disabled');
          } else {
            prevButton.disabled = false;
            prevButton.classList.remove('nr-is-disabled');
          }
        }
      }

      if (nextButton) {
        if (loop) {
          // 循环模式下，永远不禁用下一页按钮
          nextButton.disabled = false;
          nextButton.classList.remove('nr-is-disabled');
        } else {
          // 非循环模式下，使用 Swiper 的 isEnd 属性判断是否到达末尾
          // 这样可以正确处理 slidesPerView > 1 的情况
          const isEnd = swiper && swiper.isEnd !== undefined ? swiper.isEnd : (activeIndex >= totalItems - 1);
          if (isEnd) {
            nextButton.disabled = true;
            nextButton.classList.add('nr-is-disabled');
          } else {
            nextButton.disabled = false;
            nextButton.classList.remove('nr-is-disabled');
          }
        }
      }
    }
  }

  destroy() {
    this.swipers.forEach(swiper => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy();
      }
    });
    this.swipers.clear();
  }
}

// 导出类供其他模块使用
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NRSwiper;
}

// 自动初始化
function initNRSwiper() {
  if (window.nrSwiper) {
    return;
  }

  window.nrSwiper = new NRSwiper();
}

// 等待 DOM 加载完成
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNRSwiper);
} else {
  // DOM 已经加载完成，延迟一点时间确保所有元素都渲染完成
  setTimeout(initNRSwiper, 100);
}
